<template>
    <div class="bg-white flex flex-col items-center p-5 rounded-lg shadow">
        <transition
            name="fade"
            mode="out-in"
        >
            <div
                v-if="stage !== 'submittedDate' && !loading"
                class="flex-col flex items-center justify-center"
            >
                <div class="text-slate-600 text-center mb-5 font-medium">
                    {{ $t('examWidget.enter_date_header') }}
                    <i class="align-middle w-8 h-8 icon icon-badge-karma bg-purple-700"></i>
                </div>
                <transition name="fade">
                    <VueDatePicker
                        v-if="stage === 'showCalendar' && !loading"
                        v-model="selectedDate"
                        inline
                        auto-apply
                        :enable-time-picker="false"
                    />
                </transition>
                <!-- native modifier has been removed, please confirm whether the function has been affected  -->
                <BtnCta
                    variant="secondary"
                    :label="$t('examWidget.enter_date_cta')"
                    @click="changeStage()"
                />
            </div>
            <div
                v-else-if="success && !loading"
                class="flex-col flex items-center justify-center"
            >
                <div class="px-5 text-slate-600 mb-5 font-medium text-center mb-4">
                    {{ $t('examWidget.confirmation_header') }}
                </div>
                <div class="border-t border-slate-200 text-slate-400 text-sm text-center pt-4 mb-4">
                    {{ $t('examWidget.confirmation_text') }}
                </div>
                <!-- native modifier has been removed, please confirm whether the function has been affected  -->
                <BtnCta
                    variant="secondary"
                    :label="$t('examWidget.confirmation_cta')"
                    @click="changeStage(nextStage)"
                />
            </div>
            <div v-else-if="loading">
                <Spinner
                    :size="'xs'"
                    :color="'blue'"
                ></Spinner>
            </div>
        </transition>
    </div>
</template>

<script>
import Spinner from '@components/Spinner';
import { mapGetters } from 'vuex';
import { submitExamSuggestion } from '@/api/backend/course';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';

export default {
    name: 'ExamWidget',

    components: {
        VueDatePicker,
        Spinner,
    },
    emits: ['success'],

    i18nOptions: {
        namespaces: ['course'],
    },

    data() {
        return {
            stage: 'showInfo',
            nextStage: 'showCalendar',
            selectedDate: new Date(Date.now()),
            success: false,
            loading: false,
        };
    },

    computed: {
        ...mapGetters('course', ['course']),
    },

    watch: {
        stage(newValue) {
            switch (newValue) {
                case 'showInfo':
                    this.nextStage = 'showCalendar';
                    break;
                case 'showCalendar':
                    this.nextStage = 'submittedDate';
                    break;
                case 'submittedDate':
                    this.nextStage = 'removeWidget';
                    break;
                case 'removeWidget':
                    this.$emit('success');
                    break;
                default:
                    break;
            }
        },
    },

    methods: {
        formatDate(date) {
            const newDate = new Date(date);
            let month = (newDate.getMonth() + 1).toString();
            let day = newDate.getDate().toString();
            const year = newDate.getFullYear().toString();

            if (month.length < 2) month = `0${month}`;
            if (day.length < 2) day = `0${day}`;

            return [year, month, day].join('-');
        },

        changeStage() {
            if (this.stage === 'showCalendar') {
                this.loading = true;
                if (!this.validateDate()) {
                    this.onValidationError();
                    return;
                }
                const formattedDate = this.formatDate(this.selectedDate);
                submitExamSuggestion(this.course.id, formattedDate)
                    .then(() => {
                        this.stage = this.nextStage;
                        this.loading = false;
                        this.success = true;
                    })
                    .catch((e) => {
                        this.reset('showInfo');
                        throw e;
                    });
            } else this.stage = this.nextStage;
        },

        reset(stage) {
            this.selectedDate = new Date(Date.now());
            this.loading = false;
            this.stage = stage;
        },

        validateDate() {
            const today = new Date(Date.now()).setHours(0, 0, 0);
            if (this.selectedDate < today) return false;
            return true;
        },

        onValidationError() {
            const key = new Date().getTime();
            const content =
                this.selectedDate === null ? this.$t('examWidget.error_empty') : this.$t('examWidget.error_date');
            this.$store.dispatch('flashMessages/showError', { content, key }).finally(() => {
                this.reset('showCalendar');
            });
        },
    },
};
</script>
<style lang="scss" scoped>
:deep(.dp__outer_menu_wrap) {
    @apply w-full;
}

:deep(.dp__menu) {
    @apply border-0 mb-4;
}

:deep(.wrapper-exam-widget) {
    border: none !important;
    .dp__calendar_item {
        @apply flex justify-center;
    }

    .dp__date_hover:hover,
    .dp__overlay_cell:hover {
        border-width: 0 !important;
        background-color: theme('colors.blue.200') !important;
        @apply rounded-lg;
    }

    .dp__active_date,
    .dp__overlay_cell_active {
        border-radius: 0.5rem;
        color: theme('colors.white') !important;
        background-color: theme('colors.blue.600') !important;
        @apply rounded-lg;
    }
}
</style>
